import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuote } from "@/lib/quote";
import { api, formatApiError } from "@/lib/api";
import { INTEREST_OPTIONS, SITE } from "@/data/content";

const GUEST_COUNTS = ["Under 50", "50-100", "100-150", "150-250", "250+"];
const BUDGETS = ["Not sure yet", "Under $1,500", "$1,500-$3,000", "$3,000-$6,000", "$6,000+"];
const CONTACT_METHODS = ["Email", "Phone"];

function getClientToken() {
  let t = null;
  try {
    t = window.localStorage.getItem("mv_client_token");
  } catch {}
  if (!t) {
    t = (window.crypto?.randomUUID?.() || `tok-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
    try {
      window.localStorage.setItem("mv_client_token", t);
    } catch {}
  }
  return t;
}

function Field({ label, htmlFor, children, hint }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mv-label">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-[12px] text-smoke">{hint}</p>}
    </div>
  );
}

export default function InquiryForm({ mode = "quote" }) {
  const { items, palette, clearQuote, clearPalette } = useQuote();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", phone_contact_ok: false, preferred_contact: "Email",
    interests: [], date_unset: true, event_date: "", venue_city: "",
    guest_count: "", budget: "Not sure yet", style_colors: "", quantities: "",
    personalization: "", inspiration_links: "", additional_details: "", referral_source: "",
    product_needed_by_date: "", delivery_area: "", service_hours: "", floor_dimensions: "",
    marketing_permission: false, honey: "",
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | received | error
  const [receipt, setReceipt] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const set = (key) => (e) => {
    const value = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const toggleInterest = (opt) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(opt) ? f.interests.filter((i) => i !== opt) : [...f.interests, opt],
    }));

  useEffect(() => {
    if (status === "received" && mode === "quote") {
      clearQuote();
      clearPalette();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const validate = () => {
    if (form.honey) return "This request could not be verified. Please refresh and try again.";
    if (form.name.trim().length < 2) return "Please share your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return "Please share a valid email address.";
    if (form.phone && form.preferred_contact === "Phone" && !form.phone_contact_ok)
      return "Please confirm we may reach you at the phone number provided, or choose email instead.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const invalid = validate();
    if (invalid) {
      setErrorMsg(invalid);
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      phone_contact_ok: form.phone_contact_ok,
      preferred_contact: form.preferred_contact.toLowerCase(),
      interests: form.interests,
      event_date: form.date_unset ? "Date not set" : form.event_date || "Date not set",
      venue_city: form.venue_city.trim() || "Venue not chosen",
      guest_count: form.guest_count,
      budget: form.budget,
      style_colors: form.style_colors,
      quantities: form.quantities,
      personalization: form.personalization,
      inspiration_links: form.inspiration_links,
      additional_details: form.additional_details,
      referral_source: form.referral_source,
      product_needed_by_date: form.product_needed_by_date,
      delivery_area: form.delivery_area,
      service_hours: form.service_hours,
      floor_dimensions: form.floor_dimensions,
      marketing_permission: form.marketing_permission,
      palette_name: palette?.name || "",
      palette_colors: palette ? palette.colors.join(",") : "",
      monogram: palette?.monogram || "",
      quote_items: items.map((i) => ({ slug: i.slug, title: i.title, quantity: i.quantity, options: i.options || "", notes: i.notes || "" })),
      client_token: getClientToken(),
    };
    try {
      const { data } = await api.post("/inquiries", payload);
      setReceipt(data);
      setStatus("received");
    } catch (err) {
      setErrorMsg(formatApiError(err));
      setStatus("error"); // form state preserved — nothing the customer typed is lost
    }
  };

  if (status === "received" && receipt) {
    return (
      <div className="border border-olive/40 bg-parch p-8 sm:p-10" data-testid="inquiry-receipt" role="status">
        <p className="mv-label">Received — {receipt.inquiry_id}</p>
        <h3 className="mt-3 font-display text-3xl text-ink sm:text-4xl">Thank you. Your inquiry has been received.</h3>
        <p className="mt-4 max-w-xl leading-relaxed text-smoke">
          We will review your details and follow up using your preferred contact method. Your date and
          selections are confirmed only after we have agreed on the details.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/shop" className="rounded-none bg-olive px-7 py-3 text-[12px] uppercase tracking-[0.18em] text-ivory" data-testid="receipt-shop-link">
            Keep Exploring
          </Link>
          <Link to="/collections" className="border border-ink/20 px-7 py-3 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors hover:border-olive hover:text-olive" data-testid="receipt-collections-link">
            Browse Collections
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="inquiry-form" className="space-y-10">
      <p className="max-w-xl border-l-2 border-olive/50 pl-5 font-display text-xl italic leading-relaxed text-ink/80">
        Tell us a little about your celebration and the details you have in mind. It is completely fine
        if you are still deciding.
      </p>

      <section className="grid gap-6 sm:grid-cols-2">
        <Field label="Your name *" htmlFor="if-name">
          <input id="if-name" data-testid="inquiry-name-input" className="mv-input" value={form.name} onChange={set("name")} autoComplete="name" required />
        </Field>
        <Field label="Email *" htmlFor="if-email">
          <input id="if-email" data-testid="inquiry-email-input" type="email" className="mv-input" value={form.email} onChange={set("email")} autoComplete="email" required />
        </Field>
        <Field label="Phone (optional)" htmlFor="if-phone" hint="No phone? Email is perfect.">
          <input id="if-phone" data-testid="inquiry-phone-input" type="tel" className="mv-input" value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </Field>
        <Field label="Preferred contact method" htmlFor="if-contact">
          <select id="if-contact" data-testid="inquiry-contact-select" className="mv-input" value={form.preferred_contact} onChange={set("preferred_contact")}>
            {CONTACT_METHODS.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </Field>
        {form.phone && (
          <label className="flex items-start gap-3 text-sm text-ink/80 sm:col-span-2" data-testid="inquiry-phone-ok-row">
            <input type="checkbox" data-testid="inquiry-phone-ok" checked={form.phone_contact_ok} onChange={set("phone_contact_ok")} className="mt-1 h-4 w-4 accent-[#67672D]" />
            It is okay to call or text me about this inquiry.
          </label>
        )}
      </section>

      <section>
        <p className="mv-label mb-3">What are you drawn to?</p>
        <div className="flex flex-wrap gap-2.5" data-testid="inquiry-interests">
          {INTEREST_OPTIONS.map((opt) => (
            <button
              type="button"
              key={opt}
              data-testid={`inquiry-interest-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="mv-chip"
              data-on={form.interests.includes(opt)}
              onClick={() => toggleInterest(opt)}
              aria-pressed={form.interests.includes(opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      </section>

      {mode === "quote" && items.length > 0 && (
        <section className="border border-ink/10 bg-parch p-6" data-testid="inquiry-quote-summary">
          <p className="mv-label mb-3">Your quote list — carried into this inquiry</p>
          <ul className="space-y-2 text-sm">
            {items.map((i) => (
              <li key={i.slug} className="flex justify-between gap-4 text-ink/80">
                <span>
                  {i.title}
                  {i.options ? <span className="text-smoke"> — {i.options}</span> : null}
                </span>
                <span className="flex-none text-smoke">×{i.quantity}</span>
              </li>
            ))}
          </ul>
          {palette && (
            <p className="mt-3 text-sm text-olive" data-testid="inquiry-palette-line">
              Palette: {palette.name}{palette.monogram ? ` · Monogram ${palette.monogram}` : ""}
            </p>
          )}
          <p className="mt-3 text-[12px] text-smoke">Pricing confirmed in your proposal. No subtotal, no pressure.</p>
        </section>
      )}

      <section className="grid gap-6 sm:grid-cols-2">
        <Field label="Event date" htmlFor="if-date">
          {form.date_unset ? (
            <p className="mv-input text-ink/40" id="if-date">Date not set</p>
          ) : (
            <input id="if-date" data-testid="inquiry-date-input" type="date" className="mv-input" value={form.event_date} onChange={set("event_date")} />
          )}
          <label className="mt-2 flex items-center gap-2 text-sm text-ink/80">
            <input type="checkbox" data-testid="inquiry-date-unset" checked={form.date_unset} onChange={set("date_unset")} className="h-4 w-4 accent-[#67672D]" />
            Date not set
          </label>
        </Field>
        <Field label="Venue or city" htmlFor="if-venue" hint="Venue not chosen is a fine answer.">
          <input id="if-venue" data-testid="inquiry-venue-input" className="mv-input" value={form.venue_city} onChange={set("venue_city")} placeholder="Venue not chosen" />
        </Field>
      </section>

      <details className="group border-t border-ink/10 pt-6" data-testid="inquiry-optional-wedding">
        <summary className="cursor-pointer list-none">
          <span className="font-display text-2xl text-ink">A few more details</span>
          <span className="ml-3 text-[12px] uppercase tracking-[0.16em] text-smoke">Optional</span>
        </summary>
        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          <Field label="Guest count" htmlFor="if-guests">
            <select id="if-guests" data-testid="inquiry-guests-select" className="mv-input" value={form.guest_count} onChange={set("guest_count")}>
              <option value="">Select if you like</option>
              {GUEST_COUNTS.map((g) => (
                <option key={g}>{g}</option>
              ))}
            </select>
          </Field>
          <Field label="Approximate budget for these details" htmlFor="if-budget">
            <select id="if-budget" data-testid="inquiry-budget-select" className="mv-input" value={form.budget} onChange={set("budget")}>
              {BUDGETS.map((b) => (
                <option key={b}>{b}</option>
              ))}
            </select>
          </Field>
          <Field label="Style, colors, or mood" htmlFor="if-style">
            <input id="if-style" data-testid="inquiry-style-input" className="mv-input" value={form.style_colors} onChange={set("style_colors")} placeholder="Warm ivory, olive, candlelight…" />
          </Field>
          <Field label="Quantities, if you know them" htmlFor="if-qty">
            <input id="if-qty" data-testid="inquiry-quantities-input" className="mv-input" value={form.quantities} onChange={set("quantities")} placeholder="e.g. 120 place cards" />
          </Field>
          <Field label="Personalization notes" htmlFor="if-pers">
            <input id="if-pers" data-testid="inquiry-personalization-input" className="mv-input" value={form.personalization} onChange={set("personalization")} placeholder="Monograms, wording, names" />
          </Field>
          <Field label="Inspiration links" htmlFor="if-insp">
            <input id="if-insp" data-testid="inquiry-inspiration-input" className="mv-input" value={form.inspiration_links} onChange={set("inspiration_links")} placeholder="Pinterest or other links" />
          </Field>
          <Field label="Service hours, if relevant" htmlFor="if-hours" hint="For staffed services like the perfume bar or concierge.">
            <input id="if-hours" data-testid="inquiry-hours-input" className="mv-input" value={form.service_hours} onChange={set("service_hours")} placeholder="e.g. 4 hours during reception" />
          </Field>
          <Field label="Floor dimensions, if relevant" htmlFor="if-floor" hint="For dance floor or aisle designs.">
            <input id="if-floor" data-testid="inquiry-floor-input" className="mv-input" value={form.floor_dimensions} onChange={set("floor_dimensions")} placeholder="e.g. 20ft x 20ft, indoor" />
          </Field>
          <Field label="Needed-by date, for product-only requests" htmlFor="if-needed">
            <input id="if-needed" data-testid="inquiry-needed-input" type="date" className="mv-input" value={form.product_needed_by_date} onChange={set("product_needed_by_date")} />
          </Field>
          <Field label="Delivery area, for product-only requests" htmlFor="if-area">
            <input id="if-area" data-testid="inquiry-area-input" className="mv-input" value={form.delivery_area} onChange={set("delivery_area")} placeholder="e.g. Vista, CA" />
          </Field>
          <Field label="How did you find us?" htmlFor="if-ref">
            <input id="if-ref" data-testid="inquiry-referral-input" className="mv-input" value={form.referral_source} onChange={set("referral_source")} placeholder="Optional" />
          </Field>
          <Field label="Anything else" htmlFor="if-details">
            <textarea id="if-details" data-testid="inquiry-details-input" rows={4} className="mv-input resize-y" value={form.additional_details} onChange={set("additional_details")} />
          </Field>
        </div>
      </details>

      <div className="space-y-4 border-t border-ink/10 pt-6">
        <label className="flex items-start gap-3 text-sm text-ink/80" data-testid="inquiry-marketing-row">
          <input type="checkbox" data-testid="inquiry-marketing-optin" checked={form.marketing_permission} onChange={set("marketing_permission")} className="mt-1 h-4 w-4 accent-[#67672D]" />
          Email me occasional updates from Marble &amp; Veil. Optional and never required.
        </label>
        <p className="text-[12px] leading-relaxed text-smoke">
          Submitting an inquiry does not reserve a date or commit you to a purchase. See our{" "}
          <Link to="/privacy" className="border-b border-ink/30 hover:text-olive" data-testid="inquiry-privacy-link">
            privacy notice
          </Link>
          .
        </p>
        {/* honeypot — hidden from humans */}
        <input type="text" name="company" value={form.honey} onChange={set("honey")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      </div>

      {status === "error" && errorMsg && (
        <div role="alert" data-testid="inquiry-error" className="border border-red-800/30 bg-red-50 p-4 text-sm text-red-900">
          {errorMsg} Your answers are still here in the form — nothing was lost.
        </div>
      )}

      <button
        type="submit"
        data-testid="inquiry-submit-button"
        disabled={status === "submitting"}
        className="w-full rounded-none bg-olive py-4 text-[13px] uppercase tracking-[0.2em] text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:opacity-95 disabled:cursor-wait disabled:opacity-60 sm:w-auto sm:px-14"
      >
        {status === "submitting" ? "Sending…" : "Send My Inquiry"}
      </button>
      <p className="text-[12px] text-smoke">Prefer email? Write to us any time at {SITE.email}.</p>
    </form>
  );
}
